/* Reject Reason Modal
 *
 * Wide modal used across the app whenever a human rejects something (outreach
 * draft, signal, candidate). Forces a reason so the agent memory learns from
 * every rejection.
 *
 *  - Wide (max-w-2xl), keyboard-accessible
 *  - Category chips (optional) → maps to `category` field in agent_memory
 *  - Textarea 6-rows, autofocus
 *  - "Onthoud voor de agent" checkbox (default ON) → also writes to memory
 *
 * Usage:
 *   <RejectReasonModal
 *     open={showReject}
 *     onClose={() => setShowReject(false)}
 *     title="Bericht afkeuren"
 *     subtitle="LinkedIn DM aan Tom Farrow (Userlane)"
 *     originalSnippet={msg.body}
 *     onConfirm={async (reason, category, remember) => {
 *       await api.post(`/outreach/${msg.id}/reject`, { reason, category, remember });
 *     }}
 *   />
 */
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, ThumbsDown, Loader2, Sparkles } from "lucide-react";

const DEFAULT_CATEGORY_IDS = ["tone", "too_long", "too_short", "wrong_hook", "generic", "pushy", "off_topic", "other"];

export default function RejectReasonModal({
    open, onClose, onConfirm,
    title = null,
    subtitle = "",
    originalSnippet = "",
    categories = null,
    confirmLabel = null,
}) {
    const { t } = useTranslation();
    const resolvedTitle = title || t("reject.title_default");
    const resolvedConfirm = confirmLabel || t("reject.confirm_default");
    const resolvedCategories = categories || DEFAULT_CATEGORY_IDS.map((id) => ({ id, label: t(`memory.cat.${id}`) }));
    const [reason, setReason] = useState("");
    const [category, setCategory] = useState("");
    const [remember, setRemember] = useState(true);
    const [busy, setBusy] = useState(false);
    const areaRef = useRef();

    useEffect(() => {
        if (open) {
            setReason("");
            setCategory("");
            setRemember(true);
            setBusy(false);
            setTimeout(() => areaRef.current?.focus(), 60);
        }
    }, [open]);

    if (!open) return null;

    const submit = async () => {
        if (!reason.trim()) return;
        setBusy(true);
        try {
            await onConfirm?.(reason.trim(), category || null, remember);
            onClose?.();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm grid place-items-center p-4" data-testid="reject-modal">
            {/* max-h + flex-kolom: header/footer blijven altijd zichtbaar, de body scrollt op kleine schermen */}
            <div className="w-full max-w-2xl max-h-[calc(100dvh-2rem)] flex flex-col rounded-2xl border border-white/12 bg-[var(--jr-surface)] shadow-2xl">
                <header className="px-6 py-4 border-b border-white/10 flex items-start gap-3 shrink-0">
                    <div className="w-9 h-9 rounded-full bg-[var(--jr-error)]/15 grid place-items-center shrink-0">
                        <ThumbsDown size={16} className="text-[var(--jr-error)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-white leading-tight">{resolvedTitle}</h3>
                        {subtitle && <p className="text-xs text-[var(--jr-text-3)] mt-0.5 truncate">{subtitle}</p>}
                    </div>
                    <button onClick={onClose} data-testid="reject-modal-close"
                        className="p-1.5 rounded-md text-[var(--jr-text-3)] hover:text-white hover:bg-white/5">
                        <X size={16} />
                    </button>
                </header>

                <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 min-h-0" data-testid="reject-modal-body">
                    {originalSnippet && (
                        <div className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)] p-3 max-h-32 overflow-y-auto"
                            data-testid="reject-modal-original">
                            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] mb-1">{t("reject.what_rejecting")}</div>
                            <p className="text-xs text-[var(--jr-text-2)] whitespace-pre-wrap break-words leading-relaxed">
                                {originalSnippet.slice(0, 800)}
                                {originalSnippet.length > 800 ? "…" : ""}
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] block mb-2">
                            {t("reject.why")}
                            <span className="text-[var(--jr-error)] normal-case font-sans tracking-normal ml-1">*</span>
                        </label>
                        <textarea ref={areaRef} value={reason} onChange={(e) => setReason(e.target.value)}
                            rows={6} placeholder={t("reject.reason_placeholder")}
                            data-testid="reject-modal-reason"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[var(--jr-brand)] resize-none leading-relaxed" />
                        <p className="text-[10px] text-[var(--jr-text-3)] mt-1">
                            {t("reject.reason_hint")}
                        </p>
                    </div>

                    {resolvedCategories?.length > 0 && (
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] block mb-2">
                                {t("memory.category_optional")}
                            </label>
                            <div className="flex flex-wrap gap-1.5" data-testid="reject-modal-categories">
                                {resolvedCategories.map((c) => {
                                    const active = category === c.id;
                                    return (
                                        <button key={c.id} onClick={() => setCategory(active ? "" : c.id)}
                                            data-testid={`reject-cat-${c.id}`}
                                            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${active
                                                ? "bg-[var(--jr-brand)] text-black border-[var(--jr-brand)]"
                                                : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                                            {c.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <label className="flex items-center gap-2 text-xs text-[var(--jr-text-2)] cursor-pointer select-none" data-testid="reject-modal-remember">
                        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                            className="accent-[var(--jr-brand)]" />
                        <Sparkles size={12} className="text-[var(--jr-brand)]" />
                        {t("reject.remember_label")}
                    </label>
                </div>

                <footer className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-2 bg-black/20 shrink-0">
                    <button onClick={onClose} data-testid="reject-modal-cancel"
                        className="text-sm px-4 py-2 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                        {t("common.cancel")}
                    </button>
                    <button onClick={submit} disabled={!reason.trim() || busy}
                        data-testid="reject-modal-confirm"
                        className="inline-flex items-center gap-1.5 text-sm px-5 py-2 rounded-full bg-[var(--jr-error)] text-white hover:bg-[var(--jr-error)]/85 disabled:opacity-50 font-medium">
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <ThumbsDown size={13} />}
                        {resolvedConfirm}
                    </button>
                </footer>
            </div>
        </div>
    );
}
